import { ExclamationCircleIcon, TagIcon } from "@heroicons/react/24/outline"
import { useGetTagCategoriesQuery } from "~/ui-rtk/api/tagCategoriesApi"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import { getAssignedTagLabels } from "~/ui-rtk/hooks/use-structured-tags"
import { useRequiredAdTags } from "./useRequiredAdTags"

interface AdNameTagSummaryProps {
  /** Current selection: tag-category slug → TagOption ids. */
  tags: Record<string, string[]>
  /** Opens the "Build ad name from tags" composer to fix a gap. */
  onEdit: () => void
}

/**
 * Shows which tags the draft will carry onto every ad launched from it, and — if
 * the selection doesn't satisfy the company's rule — why the draft can't be
 * saved yet. Tags are mandatory: AdForgePage gates "Save as draft" on the same
 * check, so this is the explanation for a disabled button, not a soft warning.
 *
 * Renders nothing for a company with no tag categories: there's nothing to pick,
 * so nothing is demanded and an empty widget would be noise.
 */
export default function AdNameTagSummary({
  tags,
  onEdit,
}: AdNameTagSummaryProps) {
  const { data: categories } = useGetTagCategoriesQuery()
  const { labelForSlug, validate, isTaggingRequired } = useRequiredAdTags()

  const assigned = getAssignedTagLabels(categories ?? [], tags)
  const problem = validate(tags)

  if (assigned.length === 0 && !isTaggingRequired) return null

  return (
    <div className="mt-1.5 flex flex-col gap-1">
      {assigned.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {assigned.map(tag => (
            <Chip
              key={`${tag.categoryLabel}-${tag.label}`}
              className="bg-system-gray-800 text-system-gray-200 !py-0.5 !px-2 text-[11px]"
            >
              <span className="text-system-gray-500">{tag.categoryLabel}:</span>
              {tag.label}
            </Chip>
          ))}
        </div>
      )}

      {problem ? (
        <button
          type="button"
          onClick={onEdit}
          className="flex items-start gap-1 text-left text-[11px] text-system-red-400 hover:text-system-red-300"
        >
          <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span>
            {problem.needsAnyTag ? (
              <>Tags are required — add at least one to save this draft.</>
            ) : (
              <>
                Required tag{problem.missing.length > 1 ? "s" : ""} missing:{" "}
                {problem.missing.map(labelForSlug).join(", ")} — add{" "}
                {problem.missing.length > 1 ? "them" : "it"} to save this draft.
              </>
            )}
          </span>
        </button>
      ) : (
        assigned.length > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-system-gray-500">
            <TagIcon className="w-3 h-3" />
            Applied to every ad launched from this draft.
          </span>
        )
      )}
    </div>
  )
}
